import type { CaptureFailureCode } from 'ts-components/PiChat/protocol';
import { afterPaint } from 'ts-components/CustomBlockRender/dashletRenderRegistry';
import type { CaptureOutcome } from './captureUpload';

const MAX_CAPTURE_EDGE_PX = 1568;
const MAX_PIXEL_RATIO = 2;
export const NOT_RENDERED_GRACE_MS = 2_000;

export class CaptureError extends Error {
  constructor(
    readonly code: CaptureFailureCode,
    message: string
  ) {
    super(message);
    this.name = 'CaptureError';
  }
}

export interface CaptureRegion {
  width: number;
  height: number;
  style?: Record<string, string>;
}

const captureScale = (cssWidth: number, cssHeight: number) => {
  const ratio = Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO);
  const longEdge = Math.max(cssWidth, cssHeight);
  return longEdge * ratio > MAX_CAPTURE_EDGE_PX
    ? MAX_CAPTURE_EDGE_PX / longEdge
    : ratio;
};

export const nextPaint = () =>
  new Promise<void>((resolve) => afterPaint(resolve));

const finishAnimations = (element: HTMLElement) => {
  element.getAnimations({ subtree: true }).forEach((animation) => {
    try {
      animation.finish();
    } catch {
      // infinite animation
    }
  });
};

export const captureElement = async (
  element: HTMLElement,
  {
    name,
    filter,
    region,
  }: {
    name: string;
    filter?: (node: Node) => boolean;
    region?: (rect: DOMRect) => CaptureRegion;
  }
): Promise<Blob> => {
  await Promise.all([
    ...Array.from(element.querySelectorAll('img')).map((image) =>
      image.decode().catch(() => undefined)
    ),
    document.fonts.ready,
  ]);
  finishAnimations(element);
  const rect = element.getBoundingClientRect();
  const { width, height, style }: CaptureRegion = region?.(rect) ?? rect;
  if (!width || !height) {
    throw new CaptureError('not_rendered', `${name} has no size (hidden?)`);
  }
  const { domToBlob } = await import('modern-screenshot');
  return domToBlob(element, {
    scale: captureScale(width, height),
    filter,
    type: 'image/png',
    ...(region && {
      width,
      height,
      onCloneNode: (clone: Node) =>
        Object.entries(style ?? {}).forEach(([property, value]) =>
          (clone as HTMLElement).style.setProperty(property, value, 'important')
        ),
    }),
  });
};

export const replyWithCapture = async (
  reply: (outcome: CaptureOutcome) => void,
  capture: () => Promise<Blob>
) => {
  try {
    reply({ ok: true, blob: await capture() });
  } catch (error) {
    reply({
      ok: false,
      code: error instanceof CaptureError ? error.code : 'capture_failed',
      message: error instanceof Error ? error.message : String(error),
    });
  }
};
